import sanctuary from '../sanctuary.js';
const { sanctuaryCover } = sanctuary;
// Auras (2024): an Emanation that moves with its source. The server projects each aura onto the creatures inside it as
// effects (server/utils/auras.js, run after moves, map changes, turn changes and condition changes), the way Web's
// Restrained is rebuilt from its casts, so every roll reads it from combat state like any other effect. No imports:
// the caller supplies eligibility, bonuses and distances.
//
// Aura of Protection (Paladin 6): you and your allies in a 10-foot Emanation (30 feet from Aura Expansion, 18) add your
// Charisma modifier (minimum +1) to saving throws. Inactive while you're Incapacitated. A creature benefits from one
// Aura of Protection at a time, so overlapping auras give the highest bonus (then the most immunities).
// The aura also carries condition immunities: Aura of Courage (Paladin 10) for Frightened and Aura of Devotion (Oath of
// Devotion 7) for Charmed. An ally who already has the condition ignores it while inside (frightened.mjs reads this).
// Smite of Protection (Devotion 15): after the Paladin casts Divine Smite, the aura gives Half Cover until the start of
// the Paladin's next turn (`halfCover`). Holy Nimbus (Devotion 20): for 10 minutes, each enemy in the aura takes
// Radiant damage (Charisma modifier + Proficiency Bonus) when its turn starts, read from a zone effect on that enemy.
export const AURA_OF_PROTECTION = 'paladin:aura-of-protection';
export const HOLY_NIMBUS_ZONE = 'paladin:holy-nimbus-zone';

export const auraOfProtectionRadius = paladinLevel => (paladinLevel >= 18 ? 30 : 10);
export const auraOfProtectionBonus = charismaModifier => Math.max(1, Math.floor(Number(charismaModifier) || 0));

/**
 * The Aura of Protection effects for every creature inside an active aura.
 * `sources`: [{ id, name, side, bonus, radiusFeet, immunities: [{ condition, name }], halfCover, nimbusDamage }] — active
 * auras only.
 * `creatures`: [{ id, side }], where creatures on the same `side` are allies. `distanceFeet(sourceId, creatureId)`: feet between them on the map, or null when either
 * isn't placed. The source is always inside its own aura.
 */
const within = (source, id, distanceFeet) => {
  const feet = distanceFeet(String(source.id), id);
  return feet != null && feet <= source.radiusFeet;
};

export const projectAurasOfProtection = ({ sources = [], creatures = [], distanceFeet = () => null }) => creatures.flatMap(creature => {
  const id = String(creature.id);
  const inside = sources.filter(source => String(source.id) === id || (source.side === creature.side && within(source, id, distanceFeet)));
  // Highest bonus, then most immunities, then Half Cover.
  const rank = source => [source.bonus, (source.immunities || []).length, source.halfCover ? 1 : 0];
  const best = inside.reduce((winner, source) => {
    if (!winner) return source;
    const a = rank(source), b = rank(winner);
    const index = a.findIndex((value, i) => value !== b[i]);
    return index >= 0 && a[index] > b[index] ? source : winner;
  }, null);
  const immunities = (best?.immunities || []).map(({ condition, name }) => ({ condition, name }));
  return best ? [{
    id: `aura:protection:${id}`, definitionId: AURA_OF_PROTECTION, name: 'Aura of Protection', icon: '🛡', userVisible: true,
    targetCombatantId: id, sourceCombatantId: String(best.id), bonus: best.bonus, radiusFeet: best.radiusFeet,
    ...(immunities.length ? { conditionImmunities: immunities } : {}), ...(best.halfCover ? { halfCover: true } : {}),
    stackKey: `${AURA_OF_PROTECTION}:${id}`, stackPolicy: 'replace', expiration: { type: 'manual' }, removeOnSourceRemoval: false,
    description: `+${best.bonus} to saving throws${immunities.map(({ condition, name }) => `, immune to ${condition[0].toUpperCase()}${condition.slice(1)} (${name})`).join('')}${best.halfCover ? ', Half Cover (Smite of Protection)' : ''} while within ${best.radiusFeet} feet of ${best.name || 'the Paladin'}.`,
  }] : [];
});

/** Holy Nimbus zone effects on the hostile creatures inside an active Nimbus (`creatures[].hostile`); the most damage wins. */
export const projectHolyNimbus = ({ sources = [], creatures = [], distanceFeet = () => null }) => creatures.flatMap(creature => {
  const id = String(creature.id);
  if (!creature.hostile) return [];
  const best = sources.filter(source => source.nimbusDamage > 0 && String(source.id) !== id && within(source, id, distanceFeet))
    .reduce((winner, source) => (!winner || source.nimbusDamage > winner.nimbusDamage ? source : winner), null);
  return best ? [{
    id: `aura:holy-nimbus:${id}`, definitionId: HOLY_NIMBUS_ZONE, name: 'Holy Nimbus', icon: '☀', userVisible: true,
    targetCombatantId: id, sourceCombatantId: String(best.id), damage: best.nimbusDamage, stackKey: `${HOLY_NIMBUS_ZONE}:${id}`, stackPolicy: 'replace',
    expiration: { type: 'manual' }, removeOnSourceRemoval: false,
    description: `Takes ${best.nimbusDamage} Radiant damage when its turn starts in ${best.name || 'the Paladin'}'s Holy Nimbus.`,
  }] : [];
});

/** The Holy Nimbus zone effect on this creature, or null. */
export const holyNimbusZone = (combat, combatantId) => (combat?.activeEffects || [])
  .find(effect => effect?.definitionId === HOLY_NIMBUS_ZONE && String(effect.targetCombatantId) === String(combatantId)) || null;

/** Half Cover from Smite of Protection while in the aura, as a cover result, or null. */
export const auraCover = (combat, combatantId) => {
  if (sanctuaryCover(combat, combatantId)) return { degree: 'half', bonus: 2, label: 'Half Cover', sources: ['Nature’s Sanctuary'] };
  return (combat?.activeEffects || []).some(effect => effect?.definitionId === AURA_OF_PROTECTION
    && effect.halfCover === true && String(effect.targetCombatantId) === String(combatantId))
    ? { degree: 'half', bonus: 2, label: 'Half Cover', sources: ['Smite of Protection'] } : null;
};

/** The saving throw bonus as a flat roll modifier (no die), or none. */
export const auraSaveModifiers = (combat, combatantId) => {
  const bonus = Math.max(0, ...(combat?.activeEffects || []).filter(effect => effect?.definitionId === AURA_OF_PROTECTION
    && String(effect.targetCombatantId) === String(combatantId)).map(effect => Number(effect.bonus) || 0));
  return bonus ? [{ id: 'aura-of-protection', name: 'Aura of Protection', flat: bonus }] : [];
};

/** The aura feature that makes this creature immune to the condition now ('Aura of Courage'), or null. */
export const auraConditionImmunity = (combat, combatantId, condition) => {
  const wanted = String(condition || '').trim().toLowerCase().replace(/^condition:/, '');
  for (const effect of combat?.activeEffects || []) {
    if (effect?.definitionId !== AURA_OF_PROTECTION || String(effect.targetCombatantId) !== String(combatantId)) continue;
    const match = (effect.conditionImmunities || []).find(entry => entry?.condition === wanted);
    if (match) return match.name;
  }
  return null;
};

/** Every effect except the projected auras and Nimbus zones, which the projections rebuild. */
export const isProjectedAuraEffect = effect => [AURA_OF_PROTECTION, HOLY_NIMBUS_ZONE].includes(effect?.definitionId) || effect?.spellAura === true;
/**
 * Spell auras (Aura of Life, Holy Aura, Pass without Trace): a copy of the spell's effect on each ally inside its Emanation,
 * moving with the caster. `roots` are the spells' Concentration roots, with `emanationFeet`; the caster has its own recipient.
 */
export const projectSpellAuras = ({ roots = [], creatures = [], distanceFeet = () => null }) => roots.flatMap(root => {
  const casterId = String(root.sourceCombatantId);
  const side = creatures.find(creature => String(creature.id) === casterId)?.side;
  return creatures.filter(creature => String(creature.id) !== casterId && creature.side === side && within({ id: casterId, radiusFeet: root.emanationFeet }, String(creature.id), distanceFeet))
    .map(creature => ({ id: `aura:spell:${root.id}:${creature.id}`, spellAura: true, definitionId: root.definitionId, name: root.name, icon: '\u2726', userVisible: true,
      targetCombatantId: String(creature.id), sourceCombatantId: casterId, parentEffectId: root.id, concentrationId: root.concentrationId, sourceFeature: root.definitionId,
      stackKey: `${root.definitionId}:${casterId}:${creature.id}`, stackPolicy: 'refresh', expiration: { type: 'concentration', concentrationId: root.concentrationId }, removeOnSourceRemoval: true, description: root.description }));
});
export const withoutAuraEffects = (activeEffects = []) => activeEffects.filter(effect => !isProjectedAuraEffect(effect));
